/**
 * @fileoverview `<DataCard>` — carte de donnée compacte pour listes mobiles.
 *
 * Ce composant accompagne `<DataTable mobileCard>` : il standardise les cartes
 * affichées sur mobile/tablette étroite lorsque le tableau complet serait trop
 * dense. Il ne remplace pas `DataTable` : il est le rendu responsive d'une
 * ligne métier.
 */

import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/utils.ts';

/**
 * Props publiques de `DataCard`.
 */
export interface DataCardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Petit label au-dessus du titre : référence, type ou contexte. */
  eyebrow?: ReactNode;
  /** Titre principal de la ligne. Doit rester court. */
  title: ReactNode;
  /** Détail secondaire, affiché sous le titre. */
  description?: ReactNode;
  /** Zone droite : montant, statut principal ou action courte. */
  aside?: ReactNode;
  /** Métadonnées compactes : dates, acteur, statut secondaire. */
  meta?: ReactNode;
  /** Actions de fin de carte : bouton détail, validation, export. */
  footer?: ReactNode;
}

/**
 * Carte mobile standard pour une ligne de `DataTable`.
 *
 * @example
 * ```tsx
 * <DataCard
 *   eyebrow="LIQ-921555"
 *   title="AB 572 GG"
 *   aside={<Amount value={45000} />}
 *   meta={<StatusBadge kind="sgi" status="FAILED" compact />}
 *   footer={<Button size="sm">Détail</Button>}
 * />
 * ```
 */
export function DataCard({
  eyebrow,
  title,
  description,
  aside,
  meta,
  footer,
  children,
  className,
  ...props
}: DataCardProps): React.JSX.Element {
  return (
    <article
      className={cn('rounded-lg border border-border bg-card p-4 text-card-foreground', className)}
      {...props}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {eyebrow && (
            <div className="mb-1 break-all text-xs font-medium text-muted-foreground">
              {eyebrow}
            </div>
          )}
          <div className="text-quipux-navy-950 break-words text-base font-semibold">{title}</div>
          {description && <div className="mt-1 text-sm text-muted-foreground">{description}</div>}
        </div>
        {aside && <div className="shrink-0 text-right">{aside}</div>}
      </div>

      {meta && <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">{meta}</div>}
      {children && <div className="mt-3">{children}</div>}
      {footer && <div className="mt-4">{footer}</div>}
    </article>
  );
}
